import { webClient } from '../api/webClient';
import { localVault } from '../storage/localVault';
import { localDirectoryStore } from '../storage/localDirectoryStore';
import { normalizeTrustedUpdateDownloadUrl } from '../../../shared/externalUrls.js';

const LOCAL_HOME = 'Browser';
const LOCAL_PICKER_HINT =
  'Chrome may block the top-level Downloads folder. Pick or create a subfolder inside Downloads instead.';
const hasDirectoryAccessApi =
typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';

const detectBrowserLabel = () => {
  if (typeof navigator === 'undefined') return 'this browser';
  const ua = navigator.userAgent || '';
  if (/firefox\//i.test(ua)) return 'Firefox';
  if (/edg\//i.test(ua)) return 'Edge';
  if (/chrome\//i.test(ua) && !/edg\//i.test(ua)) return 'Chrome';
  if (/safari\//i.test(ua) && !/chrome\//i.test(ua)) return 'Safari';
  return 'this browser';
};

const localFsReason = hasDirectoryAccessApi ?
'' :
`Local filesystem browsing is not supported in ${detectBrowserLabel()}. Use Chrome/Edge or the desktop app.`;
const hasLocalVault = localVault.isSupported();
const localVaultReason = hasLocalVault ?
'' :
`Encrypted local login storage is not supported in ${detectBrowserLabel()}.`;

let localRootHandle = null;
let localRootName = '';
let restoreLocalRootPromise = null;
const directoryHandleCache = new Map();
const fileHandleCache = new Map();

const unsupported = (feature) => {
  throw new Error(`${feature} is not available in web mode.`);
};

const normalizePathInput = (value) =>
String(value || '').
replace(/\\/g, '/').
replace(/\/+/g, '/').
replace(/^\/+|\/+$/g, '');

const splitPath = (value) => normalizePathInput(value).split('/').filter(Boolean);

const getLocalRootPath = () => {
  const rootName = String(localRootName || '').trim();
  return rootName ? `${LOCAL_HOME}/${rootName}` : LOCAL_HOME;
};

const toRelativeSegments = (targetPath) => {
  let parts = splitPath(targetPath);
  if (parts[0]?.toLowerCase() === LOCAL_HOME.toLowerCase()) {
    parts = parts.slice(1);
  }
  if (localRootName && parts[0]?.toLowerCase() === localRootName.toLowerCase()) {
    parts = parts.slice(1);
  }
  return parts;
};

const toDisplayPath = (segments = []) => {
  const tail = segments.filter(Boolean).join('/');
  const root = getLocalRootPath();
  return tail ? `${root}/${tail}` : root;
};

const cacheKey = (segments) => segments.join('/');

const resetLocalCaches = () => {
  directoryHandleCache.clear();
  fileHandleCache.clear();
  if (localRootHandle) {
    directoryHandleCache.set('', localRootHandle);
  }
};

const setLocalRootHandle = async (handle, { persist = true } = {}) => {
  localRootHandle = handle || null;
  localRootName = String(localRootHandle?.name || '').trim();
  resetLocalCaches();
  if (!localDirectoryStore.isSupported()) {
    return localRootHandle;
  }
  if (!persist) {
    return localRootHandle;
  }
  if (localRootHandle) {
    await localDirectoryStore.setRootHandle(localRootHandle).catch(() => {});
  } else {
    await localDirectoryStore.clearRootHandle().catch(() => {});
  }
  return localRootHandle;
};

const restoreLocalRootHandle = async ({ requestPermission = false } = {}) => {
  if (!hasDirectoryAccessApi || localRootHandle || !localDirectoryStore.isSupported()) {
    return localRootHandle;
  }
  if (!restoreLocalRootPromise) {
    restoreLocalRootPromise = (async () => {
      const storedHandle = await localDirectoryStore.getRootHandle();
      if (!storedHandle) return null;
      try {
        let permission = 'granted';
        if (typeof storedHandle.queryPermission === 'function') {
          permission = await storedHandle.queryPermission({ mode: 'readwrite' });
        }
        if (permission !== 'granted' &&
            requestPermission &&
            typeof storedHandle.requestPermission === 'function') {
          permission = await storedHandle.requestPermission({ mode: 'readwrite' });
        }
        if (permission !== 'granted') {
          return null;
        }
        await setLocalRootHandle(storedHandle, { persist: false });
        return storedHandle;
      } catch {
        await localDirectoryStore.clearRootHandle().catch(() => {});
        return null;
      } finally {
        restoreLocalRootPromise = null;
      }
    })();
  }
  return restoreLocalRootPromise;
};

const ensureLocalRoot = () => {
  if (!hasDirectoryAccessApi) {
    throw new Error('Local filesystem access is not available in this browser.');
  }
  if (!localRootHandle) {
    throw new Error(`Choose a local folder first. ${LOCAL_PICKER_HINT}`);
  }
  return localRootHandle;
};

const getDirectoryHandle = async (segments, options = {}) => {
  const { create = false } = options;
  const root = ensureLocalRoot();
  const cleanSegments = Array.isArray(segments) ? segments.filter(Boolean) : [];
  const key = cacheKey(cleanSegments);
  const cached = directoryHandleCache.get(key);
  if (cached) return cached;

  let current = root;
  const walked = [];
  for (const segment of cleanSegments) {
    current = await current.getDirectoryHandle(segment, { create });
    walked.push(segment);
    directoryHandleCache.set(cacheKey(walked), current);
  }
  return current;
};

const parseFileTarget = (targetPath) => {
  const fileSegments = toRelativeSegments(targetPath);
  if (fileSegments.length === 0) {
    throw new Error('Invalid local path.');
  }
  const name = fileSegments[fileSegments.length - 1];
  const parentSegments = fileSegments.slice(0, -1);
  return { name, parentSegments, fileSegments };
};

const getFileHandle = async (targetPath, options = {}) => {
  const { create = false } = options;
  const { name, parentSegments, fileSegments } = parseFileTarget(targetPath);
  const key = cacheKey(fileSegments);
  const cached = fileHandleCache.get(key);
  if (cached) return cached;

  const parent = await getDirectoryHandle(parentSegments, { create });
  const handle = await parent.getFileHandle(name, { create });
  fileHandleCache.set(key, handle);
  return handle;
};

const createDuplicateLocalNameError = (name) =>
  new Error(`A file or folder named "${name}" already exists.`);

const assertLocalDirectoryTargetAvailable = async (parentHandle, name) => {
  try {
    await parentHandle.getDirectoryHandle(name);
    throw createDuplicateLocalNameError(name);
  } catch (err) {
    if (err?.name === 'NotFoundError') {
      return;
    }
    if (err?.name === 'TypeMismatchError') {
      throw createDuplicateLocalNameError(name);
    }
    if (err?.message === createDuplicateLocalNameError(name).message) {
      throw err;
    }
    throw err;
  }
};

const listLocalDirectory = async (targetPath) => {
  if (!hasDirectoryAccessApi) {
    return { base: LOCAL_HOME, items: [] };
  }
  await restoreLocalRootHandle();
  if (!localRootHandle) {
    return { base: getLocalRootPath(), items: [] };
  }

  const requestedSegments = toRelativeSegments(targetPath);
  let baseSegments = requestedSegments;
  let directory = null;

  try {
    directory = await getDirectoryHandle(requestedSegments);
  } catch {
    baseSegments = [];
    directory = ensureLocalRoot();
  }

  const items = [];
  for await (const [name, handle] of directory.entries()) {
    const entrySegments = [...baseSegments, name];
    if (handle.kind === 'directory') {
      directoryHandleCache.set(cacheKey(entrySegments), handle);
      items.push({
        name,
        path: toDisplayPath(entrySegments),
        type: 'dir',
        size: null,
        modified: null
      });
      continue;
    }

    fileHandleCache.set(cacheKey(entrySegments), handle);
    let size = null;
    let modified = null;
    try {
      const file = await handle.getFile();
      size = Number.isFinite(file?.size) ? file.size : null;
      modified = Number.isFinite(file?.lastModified) ? file.lastModified : null;
    } catch {
      // Ignore metadata failures.
    }
    items.push({
      name,
      path: toDisplayPath(entrySegments),
      type: 'file',
      size,
      modified
    });
  }

  return {
    base: toDisplayPath(baseSegments),
    items: items.sort((a, b) => {
      if (a.type === b.type) return a.name.localeCompare(b.name);
      return a.type === 'dir' ? -1 : 1;
    })
  };
};

const chooseLocalDirectory = async () => {
  if (!hasDirectoryAccessApi) {
    throw new Error('Local filesystem access is not available in this browser.');
  }
  const pickedHandle = await window.showDirectoryPicker({
    mode: 'readwrite',
    id: 'kinetic-local-root',
    startIn: 'downloads'
  });
  await setLocalRootHandle(pickedHandle);
  return listLocalDirectory(getLocalRootPath());
};

const writeBlobToLocalPath = async (targetPath, blob) => {
  const fileHandle = await getFileHandle(targetPath, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
};

const removeLocalEntry = async (targetPath) => {
  const { name, parentSegments, fileSegments } = parseFileTarget(targetPath);
  const parent = await getDirectoryHandle(parentSegments);
  if (typeof parent.removeEntry !== 'function') {
    throw new Error('Delete is not supported by this browser.');
  }
  await parent.removeEntry(name, { recursive: true });
  const prefix = cacheKey(fileSegments);
  for (const key of [...directoryHandleCache.keys()]) {
    if (key === prefix || key.startsWith(`${prefix}/`)) {
      directoryHandleCache.delete(key);
    }
  }
  for (const key of [...fileHandleCache.keys()]) {
    if (key === prefix || key.startsWith(`${prefix}/`)) {
      fileHandleCache.delete(key);
    }
  }
  if (localRootHandle) {
    directoryHandleCache.set('', localRootHandle);
  }
};

const triggerDownload = (blob, fileName) => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName || 'download.bin';
  anchor.click();
  URL.revokeObjectURL(objectUrl);
};

export const createWebApiBridge = () => ({
  mode: 'web',
  capabilities: {
    windowControls: false,
    localFs: hasDirectoryAccessApi,
    localFsReason,
    transfer: true,
    archive: true,
    nbt: false,
    vault: hasLocalVault,
    vaultReason: localVaultReason,
    deepLink: false
  },

  async connect(config, tabId) {
    return webClient.connect(config, tabId || 'default');
  },

  async disconnect(tabId) {
    await webClient.disconnect(tabId || null);
    return { ok: true };
  },

  async getSessionStatus() {
    return webClient.getSessionStatus();
  },

  async setActiveTab(tabId) {
    if (tabId) await webClient.setActiveConnection(tabId).catch(() => {});
    return { ok: true };
  },

  async listRemote(remotePath, options) {
    return webClient.listRemote(remotePath, options);
  },

  async folderSize(remotePath) {
    return webClient.folderSize(remotePath);
  },

  async readRemoteFile(remotePath) {
    return webClient.readRemoteFile(remotePath);
  },

  async readRemoteFileChunk(remotePath, offset, length) {
    return webClient.readRemoteFileChunk(remotePath, offset, length);
  },

  async readRemoteFileAsDataUrl(remotePath) {
    const blob = await webClient.downloadFile(remotePath);
    return URL.createObjectURL(blob);
  },

  async writeRemoteFile(remotePath, content) {
    await webClient.writeRemoteFile(remotePath, content);
    return { ok: true };
  },

  async createRemoteFile(remotePath, content = '') {
    await webClient.createRemoteFile(remotePath, content);
    return { ok: true };
  },

  async createRemoteDir(remotePath) {
    await webClient.createRemoteDir(remotePath);
    return { ok: true };
  },

  async renameRemote(from, to) {
    await webClient.renameRemote(from, to);
    return { ok: true };
  },

  async moveRemote(from, to) {
    await webClient.renameRemote(from, to);
    return { ok: true };
  },

  async deleteRemote(remotePath) {
    await webClient.deleteRemote(remotePath);
    return { ok: true };
  },

  async upload(localPath, remotePath, options) {
    if (localPath instanceof Blob) {
      await webClient.uploadFile(remotePath, localPath, options);
      return { ok: true };
    }
    if (typeof localPath === 'string' && hasDirectoryAccessApi && localRootHandle) {
      const fileHandle = await getFileHandle(localPath);
      const file = await fileHandle.getFile();
      await webClient.uploadFile(remotePath, file, options);
      return { ok: true };
    }
    unsupported('Path-based upload');
  },

  async uploadBrowserFile(file, remotePath, options) {
    await webClient.uploadFile(remotePath, file, options);
    return { ok: true };
  },

  async download(remotePath, localPath, options = {}) {
    const blob = await webClient.downloadFile(remotePath, options);
    if (typeof localPath === 'string' && hasDirectoryAccessApi && localRootHandle) {
      await writeBlobToLocalPath(localPath, blob);
      return { ok: true };
    }
    const baseName = String(remotePath || '').split('/').filter(Boolean).pop() || 'download';
    const isZip = blob.type === 'application/zip';
    const fileName = isZip && !baseName.endsWith('.zip') ? `${baseName}.zip` : baseName;
    triggerDownload(blob, fileName);
    return { ok: true };
  },

  async listLocal(targetPath) {
    return listLocalDirectory(targetPath);
  },

  async readLocalFile(targetPath) {
    const fileHandle = await getFileHandle(targetPath);
    const file = await fileHandle.getFile();
    return file.text();
  },

  async writeLocalFile(targetPath, content) {
    const text = String(content ?? '');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    if (hasDirectoryAccessApi && localRootHandle) {
      await writeBlobToLocalPath(targetPath, blob);
      return { ok: true };
    }
    const suggested = String(targetPath || '').split(/[\\/]/).pop() || 'kinetic-export.txt';
    triggerDownload(blob, suggested);
    return { ok: true };
  },

  async createLocalDir(targetPath) {
    const segments = toRelativeSegments(targetPath);
    if (segments.length === 0) {
      throw new Error('Invalid local path.');
    }
    const name = segments[segments.length - 1];
    const parentSegments = segments.slice(0, -1);
    const parent = await getDirectoryHandle(parentSegments, { create: true });
    await assertLocalDirectoryTargetAvailable(parent, name);
    await parent.getDirectoryHandle(name, { create: true });
    return { ok: true };
  },

  async deleteLocal(targetPath) {
    await removeLocalEntry(targetPath);
    return { ok: true };
  },

  async getHomeDir() {
    await restoreLocalRootHandle();
    return getLocalRootPath();
  },

  async pickLocalDirectory() {
    return chooseLocalDirectory();
  },

  async transferConnect(side, config) {
    return webClient.transferConnect(side, config);
  },

  async transferList(side, remotePath, options) {
    return webClient.transferList(side, remotePath, options);
  },

  async transferCopy(payload) {
    return webClient.transferCopy(payload);
  },

  async transferZipMove(payload) {
    // Web mode: treat zip-move as a regular copy
    return webClient.transferCopy(payload);
  },

  async archiveRemote(paths, dest) {
    await webClient.archiveRemote(paths, dest);
  },

  async unarchiveRemote(zipPath, destDir) {
    await webClient.unzipRemote(zipPath, destDir);
  },

  async nbtLoad() {
    unsupported('NBT tools');
  },

  async nbtSave() {
    unsupported('NBT tools');
  },

  async nbtRegionInfo() {
    unsupported('NBT tools');
  },

  async nbtLoadChunk() {
    unsupported('NBT tools');
  },

  async nbtSaveChunk() {
    unsupported('NBT tools');
  },

  async readNbt() {
    unsupported('NBT tools');
  },

  async writeNbt() {
    unsupported('NBT tools');
  },

  async readRegionHeaders() {
    unsupported('NBT tools');
  },

  async readRegionChunk() {
    unsupported('NBT tools');
  },

  async writeRegionChunk() {
    unsupported('NBT tools');
  },

  async minimizeWindow() {},

  async closeWindow() {},

  async openExternal(url) {
    const safeUrl = normalizeTrustedUpdateDownloadUrl(url);
    window.open(safeUrl, '_blank', 'noopener,noreferrer');
  },

  async setSecret() {
    return { ok: true };
  },

  async getSecret() {
    return null;
  },

  async deleteSecret() {
    return { ok: true };
  },

  async vaultStatus() {
    if (!hasLocalVault) {
      return { hasMaster: false, unlocked: false };
    }
    return localVault.status();
  },

  async vaultSetMaster(password) {
    if (!hasLocalVault) {
      return { ok: false, error: localVaultReason || 'Vault is not available.' };
    }
    const result = await localVault.setMaster(password);
    if (!result?.ok) {
      throw new Error(result?.error || 'Unable to set master password.');
    }
    return result;
  },

  async vaultUnlock(password) {
    if (!hasLocalVault) {
      return { ok: false, error: localVaultReason || 'Vault is not available.' };
    }
    const result = await localVault.unlock(password);
    if (!result?.ok) {
      throw new Error(result?.error || 'Unable to unlock vault.');
    }
    return result;
  },

  async vaultList() {
    if (!hasLocalVault) return [];
    return localVault.list();
  },

  async vaultGet(name) {
    if (!hasLocalVault) {
      throw new Error(localVaultReason || 'Vault is not available.');
    }
    return localVault.get(name);
  },

  async vaultSave(payload) {
    if (!hasLocalVault) {
      return { ok: false, error: localVaultReason || 'Vault is not available.' };
    }
    await localVault.save(payload?.entry || {}, Boolean(payload?.encrypt));
    return { ok: true };
  },

  async vaultDelete(name) {
    if (!hasLocalVault) return { ok: true };
    await localVault.delete(name);
    return { ok: true };
  },

  onDeepLink() {
    return () => {};
  }
});
